import React, { forwardRef, useRef } from 'react';
import { BorderTrail } from './BorderTrail';
import { getMappedKey } from '../utils/ipInputUtils';
import { isValidInputFormat } from '../utils/ipMath';

// Design agent: Spec 010 — centralises the "does the user currently prefer reduced motion" check
// so every scroll-into-view call in this file (and any other field that reuses
// scrollFieldIntoViewOnFirstCharacter below, e.g. the VLSM host-count row) reacts the same way,
// matching the reduced-motion gating already applied to decorative animations in App.css.
// Developer agent: `Element.scrollIntoView`'s behavior option only accepts 'smooth' or 'auto' —
// 'auto' is the browser's immediate, non-animated jump, so that is what is used when the OS-level
// setting asks for reduced motion.
const getScrollBehavior = () => {
    const prefersReducedMotion = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return prefersReducedMotion ? 'auto' : 'smooth';
};

// Design agent: Spec 010 — a field should come to the vertical centre of the viewport the moment
// the user types its first character, not merely when focus lands on it (Tab / auto-advance already
// move focus without needing a scroll, since the programmatic focus() calls elsewhere in the app
// pass `preventScroll: true` precisely to avoid that). Exposed as a plain helper — not a Hook — so
// it can be called directly from imperative onChange handlers, including call sites that render a
// dynamic, growing/shrinking list of plain <input> fields (the VLSM host rows) where calling a Hook
// once per list row would break the Rules of Hooks.
// Developer agent: Compares the previous and next raw values' emptiness and scrolls `element` into
// view exactly on the empty -> non-empty transition; typing further characters, or characters in an
// already non-empty field, does not re-trigger it until the field is cleared back to empty first.
export const scrollFieldIntoViewOnFirstCharacter = (previousValue, nextValue, element) => {
    const wasEmpty = (previousValue ?? '').trim() === '';
    const isEmptyNow = (nextValue ?? '').trim() === '';
    if (wasEmpty && !isEmptyNow && element) {
        element.scrollIntoView({ block: 'center', behavior: getScrollBehavior() });
    }
};

// Design agent: Spec 010 — the "↵ Enter adds a new line" nudge is shared between IpInputField and
// the VLSM host-count row (a plain <input> that does not render through IpInputField), so both
// surfaces show the exact same chip instead of drifting apart visually.
// Developer agent: Presentational only — the caller decides visibility (focused + non-empty + not at
// the field cap) and passes `hasNeighbourAction` when another absolutely-positioned action (e.g. the
// inspector's "Belong ?" button) already occupies the same corner, so the chip shifts left instead
// of overlapping it.
export const EnterHintChip = ({ id, hasNeighbourAction = false }) => (
    <span
        className={`input-enter-hint${hasNeighbourAction ? ' input-enter-hint-shifted' : ''}`}
        id={id}
        aria-hidden="true"
    >
        <span className="input-enter-hint-key">↵</span> Enter
    </span>
);

export const IpInputField = forwardRef(({
    index,
    value,
    isActive,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    showFormatHint,
    // Design agent: Spec 005 — lets the parent inspector outline this field when it is one half
    // of an overlapping pair the user is currently hovering in the Overall Summary card.
    // Developer agent: Defaults to false so every existing call site keeps working unchanged.
    isHighlighted = false,
    // Design agent: Spec 010 — lets the parent inspector/planner tell this field that no more rows
    // can be appended (the shared list-normalisation helper stops growing the list once it hits its
    // cap), so the "↵ Enter" hint chip never promises an action that Enter would not actually do.
    // Developer agent: Defaults to false so every existing call site keeps working unchanged.
    isAtFieldLimit = false,
    children,
}, ref) => {

    const internalRef = useRef(null);

    const handleRef = (node) => {
        internalRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    };

    // Design agent: Spec 010 — funnels every path that can change this field's text (native typing
    // and paste via the <input>'s own onChange, plus the digit-remapping shortcut below which calls
    // preventDefault() and bypasses that native event) through one place, so the
    // first-keystroke-scrolls-the-field-into-view rule fires exactly once per real change.
    // Developer agent: Compares this render's `value` prop (the text before the change) against the
    // text about to replace it, then forwards to the parent's onChange unchanged.
    const commitChange = (nextValue, caretPosition) => {
        scrollFieldIntoViewOnFirstCharacter(value, nextValue, internalRef.current);
        onChange(nextValue, caretPosition);
    };

    const handleLocalKeyDown = (event) => {
        const input = event.target;
        const charBeforeCursor = input.selectionStart > 0 ? input.value[input.selectionStart - 1] : null;
        const mapped = getMappedKey(event.key, charBeforeCursor);
        if (mapped) {
            event.preventDefault();
            const input = event.target;
            const start = input.selectionStart;
            const end = input.selectionEnd;
            const oldValue = input.value;
            const newValue = oldValue.slice(0, start) + mapped + oldValue.slice(end);
            const newCursor = start + 1;

            // Propagate changes
            commitChange(newValue, newCursor);

            // Force cursor update
            requestAnimationFrame(() => {
                if (internalRef.current) {
                    internalRef.current.setSelectionRange(newCursor, newCursor);
                }
            });
        }

        if (onKeyDown) {
            onKeyDown(event);
        }
    };

    const isValid = isValidInputFormat(value);
    const isNotEmpty = value.trim() !== '';
    const showInvalidError = !isActive && !isValid && isNotEmpty;

    // Design agent: Spec 010 — the "↵ Enter" hint only makes sense while the user is looking at a
    // field they are actively filling in; it disappears once they move on (isActive becomes false)
    // or once the list cannot grow further (isAtFieldLimit), so it never advertises an action Enter
    // would not actually perform.
    // Developer agent: Plain boolean, recomputed every render from the props/local state above.
    const showEnterHint = isActive && isNotEmpty && !isAtFieldLimit;

    return (
        <label
            className="field"
            htmlFor={`ipv4-insight-input-${index}`}
            id={`ipv4-insight-label-${index}`}
        >
            <span id={`ipv4-insight-input-label-${index}`}>Field {index + 1}</span>
            <BorderTrail
                className={`field-input-wrapper${isHighlighted ? ' is-overlap-highlighted' : ''}`}
                color={(isActive && !isValid && isNotEmpty) ? "rgb(239, 68, 68)" : "transparent"}
                trailSize={300}
            >
                <input
                    ref={handleRef}
                    id={`ipv4-insight-input-${index}`}
                    value={value}
                    placeholder="e.g. 10.0.0.125/24"
                    className="field-input"
                    onChange={(e) => commitChange(e.target.value, e.target.selectionStart)}
                    onKeyDown={handleLocalKeyDown}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    autoComplete="off"
                    style={{
                        width: '100%',
                        borderColor: showInvalidError ? 'rgb(239, 68, 68)' : undefined
                    }}
                />
                {children}
                {showEnterHint && (
                    <EnterHintChip
                        id={`ipv4-insight-input-enter-hint-${index}`}
                        hasNeighbourAction={Boolean(children)}
                    />
                )}
            </BorderTrail>

            {showInvalidError && (
                <span className="error-text" style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                    invalid format
                </span>
            )}

            {showFormatHint && !isValid && (
                <div className="format-indicator" style={{
                    marginTop: '0.75rem',
                    padding: '0.85rem',
                    background: 'rgba(var(--md-sys-color-surface-rich-rgb), 0.6)',
                    borderRadius: '12px',
                    border: '1px solid rgba(var(--md-sys-color-outline-rgb), 0.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.65rem'
                }}>
                    <p style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'rgba(var(--md-sys-color-on-surface-rgb), 0.8)',
                        margin: 0,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        paddingLeft: '0.2rem'
                    }}>
                        Valid Formats
                    </p>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                        gap: '0.5rem',
                    }}>
                        {[
                            { label: 'network', example: '10.0.0.0/24' },
                            { label: 'ip', example: '192.168.1.5' },
                            { label: 'cidr', example: '/27' },
                            { label: 'full mask', example: '255.255.255.0' },
                        ].map((item) => (
                            <div key={item.label} style={{
                                background: 'rgba(var(--md-sys-color-surface-subtle-rgb), 0.5)',
                                padding: '0.5rem 0.75rem',
                                borderRadius: '8px',
                                border: '1px solid rgba(var(--md-sys-color-outline-rgb), 0.1)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.15rem'
                            }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--md-sys-color-primary)' }}>
                                    {item.label}
                                </span>
                                <span style={{ fontSize: '0.7rem', color: 'rgba(var(--md-sys-color-on-surface-rgb), 0.5)', fontFamily: 'monospace' }}>
                                    {item.example}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gap: '0.5rem',
                    }}>
                        {[
                            { label: 'network - network', example: '192.168.0.0/24 - 192.168.5.0/24' },
                            { label: 'ip - ip', example: '10.0.0.50 - 10.0.0.150' },
                        ].map((item) => (
                            <div key={item.label} style={{
                                background: 'rgba(var(--md-sys-color-surface-subtle-rgb), 0.5)',
                                padding: '0.5rem 0.75rem',
                                borderRadius: '8px',
                                border: '1px solid rgba(var(--md-sys-color-outline-rgb), 0.1)',
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '0.25rem'
                            }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--md-sys-color-tertiary)' }}>
                                    {item.label}
                                </span>
                                <span style={{ fontSize: '0.7rem', color: 'rgba(var(--md-sys-color-on-surface-rgb), 0.5)', fontFamily: 'monospace' }}>
                                    {item.example}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </label>
    );
});

IpInputField.displayName = 'IpInputField';
